import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Listbox } from "@headlessui/react";
import toast from "react-hot-toast";

// MultiSelect Component
function MultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder,
  label,
}) {
  const [selected, setSelected] = useState(selectedValues || []);

  // Update local state when selectedValues prop changes
  useEffect(() => {
    setSelected(selectedValues || []);
  }, [selectedValues]);

  const handleSelectionChange = (newSelection) => {
    setSelected(newSelection);
    onChange(newSelection);
  };

  const toggleOption = (optionValue) => {
    const newSelection = selected.includes(optionValue)
      ? selected.filter((item) => item !== optionValue)
      : [...selected, optionValue];
    handleSelectionChange(newSelection);
  };

  return (
    <div className="relative">
      <Listbox value={selected} onChange={handleSelectionChange}>
        <div className="relative">
          <Listbox.Button className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left">
            <span className="block truncate">
              {selected.length === 0 ? placeholder : selected.join(", ")}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => {
              const optionValue = option.description || option;
              const isSelected = selected.includes(optionValue);

              return (
                <div
                  key={option.id || option}
                  className="relative cursor-pointer select-none py-2 px-3 hover:bg-primary-100 hover:text-primary-900"
                  onClick={() => toggleOption(optionValue)}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="block truncate">{optionValue}</span>
                  </div>
                </div>
              );
            })}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

function VideoEntry() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [units, setUnits] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [existingCoverImage, setExistingCoverImage] = useState("");

  // Collections data
  const [unitsData, setUnitsData] = useState([]);

  // Vimeo video selection state
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [cachedVideos, setCachedVideos] = useState([]);

  // Fetch units from Firestore
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const unitsRef = collection(db, "units");
        const q = query(unitsRef, orderBy("description"));
        const querySnapshot = await getDocs(q);

        const unitsList = [];
        querySnapshot.forEach((doc) => {
          unitsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setUnitsData(unitsList);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnits();
  }, []);

  // Fetch video data
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        if (videoId === "new") {
          setVideo({ id: "new" });
          setLoading(false);
          return;
        }

        const videoRef = doc(db, "videos", videoId);
        const videoSnap = await getDoc(videoRef);

        if (videoSnap.exists()) {
          const videoData = {
            id: videoSnap.id,
            ...videoSnap.data(),
          };
          setVideo(videoData);
          setTitle(videoData.title || "");
          setDescription(videoData.description || "");
          setUnits(videoData.units || []);
          setVideoUrl(videoData.video_url || "");
          // Set existing cover image if available
          if (videoData.cover_image) {
            setCoverImagePreview(videoData.cover_image);
            setExistingCoverImage(videoData.cover_image);
          }
        } else {
          setError("Video not found");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching video:", error);
        setError("Failed to load video");
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setCoverImage(null);
    setCoverImagePreview(existingCoverImage || "");
  };

  const handleSave = async () => {
    try {
      let coverImageUrl = existingCoverImage; // Preserve existing image URL

      // Upload cover image if a new one is selected
      if (coverImage) {
        const timestamp = Date.now();
        const fileName = `images/videos/${timestamp}_${coverImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, coverImage);
        coverImageUrl = await getDownloadURL(uploadResult.ref);
      }

      const videoData = {
        title,
        description,
        units,
        video_url: videoUrl,
        cover_image: coverImageUrl,
        updated_at: new Date(),
      };

      // Determine if this is a new video or existing video
      let isNewVideo = video.id === "new";

      // If we have a video ID that's not "new", verify the video exists
      if (!isNewVideo && video.id) {
        try {
          const videoRef = doc(db, "videos", video.id);
          const videoSnap = await getDoc(videoRef);
          isNewVideo = !videoSnap.exists();
        } catch (error) {
          console.error("Error checking video existence:", error);
          // If we can't verify, assume it's new to be safe
          isNewVideo = true;
        }
      }

      if (isNewVideo) {
        // Create new video document
        const videosRef = collection(db, "videos");
        const docRef = await addDoc(videosRef, {
          ...videoData,
          created_date: new Date(),
        });

        // Update the video state with the new ID
        setVideo({ id: docRef.id, ...videoData, created_date: new Date() });

        toast.success("Video saved successfully!");
      } else {
        // Update existing video document
        const videoRef = doc(db, "videos", video.id);
        await updateDoc(videoRef, videoData);

        toast.success("Video updated successfully!");
      }
    } catch (error) {
      console.error("Error saving video:", error);
      toast.error("Error saving video. Please try again.");
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    navigate("/admin/videos");
  };

  const handleSaveAndNew = async () => {
    await handleSave();
    // Reset form for new video
    setTitle("");
    setDescription("");
    setUnits([]);
    setVideoUrl("");
    setCoverImage(null);
    setCoverImagePreview("");
    setExistingCoverImage("");
    setVideo({ id: "new" });
  };

  const handleCancel = () => {
    const hasChanges =
      title || description || units.length > 0 || videoUrl || coverImage;

    if (hasChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmed) {
        return;
      }
    }

    navigate("/admin/videos");
  };

  const openVideoDialog = async () => {
    if (!title.trim()) {
      toast.error("Please enter a video title first to search for videos.");
      return;
    }

    setShowVideoDialog(true);

    // If we have cached videos, use them
    if (cachedVideos.length > 0) {
      const videoTitle = title.toLowerCase();
      const filtered = cachedVideos.filter((video) =>
        video.name.toLowerCase().includes(videoTitle)
      );
      setFilteredVideos(filtered);
      return;
    }

    // Otherwise, fetch videos from API
    setLoadingVideos(true);

    try {
      const videoTitle = title.toLowerCase();
      let allVideos = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await fetch(
          `https://api.vimeo.com/users/129001864/projects/26591581/videos?page=${page}&per_page=100`,
          {
            headers: {
              Authorization: "Bearer 71784e0083b4a9fccd6b1cb1ec310a0f",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const videosList = data.data.map((video) => ({
          id: video.uri.split("/").pop(),
          name: video.name,
          description: video.description,
          player_url: video.player_embed_url,
          duration: video.duration,
          created_time: video.created_time,
        }));

        allVideos = [...allVideos, ...videosList];

        // Check if there are more pages
        if (data.paging && data.paging.next) {
          page++;
        } else {
          hasMorePages = false;
        }
      }

      // Cache all videos for this session
      setCachedVideos(allVideos);

      // Filter videos by title only
      const filtered = allVideos.filter((video) =>
        video.name.toLowerCase().includes(videoTitle)
      );

      setFilteredVideos(filtered);
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error("Error loading videos. Please try again.");
    } finally {
      setLoadingVideos(false);
    }
  };

  const selectVideo = (video) => {
    const videoUrl =
      video.player_url || `https://player.vimeo.com/video/${video.id}`;
    setVideoUrl(videoUrl);
    setShowVideoDialog(false);
    toast.success("Video selected successfully!");
  };

  const closeVideoDialog = () => {
    setShowVideoDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading video...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">
              {video?.id === "new" ? "New Video" : "Edit Video"}
            </h1>
          </div>

          <div className="flex">
            {/* Left Column */}
            <div className="flex-1 pr-8">
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter video title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter video description"
                  />
                </div>

                {/* Units */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Units
                  </label>
                  <MultiSelect
                    options={unitsData}
                    selectedValues={units}
                    onChange={setUnits}
                    placeholder="Please select units..."
                  />
                </div>

                {/* Video URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter video URL or click 'Select Video' to choose from Vimeo"
                    />
                    <button
                      type="button"
                      onClick={openVideoDialog}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Select Video
                    </button>
                  </div>
                  {videoUrl && (
                    <p className="mt-1 text-sm text-gray-500">
                      Video URL: {videoUrl}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="w-1/2 pl-8">
              <div className="space-y-6">
                {/* Upload Cover */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="cover-upload"
                  />
                  <div className="flex space-x-2">
                    <label
                      htmlFor="cover-upload"
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors cursor-pointer inline-block"
                    >
                      Upload Cover
                    </label>
                    {(coverImage || existingCoverImage) && (
                      <button
                        type="button"
                        onClick={handleClearImage}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="mt-2 w-[360px] h-[233px] bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
                    {coverImagePreview ? (
                      <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-xs">
                        No image selected
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            {video.id === "new" ? (
              <>
                <button
                  onClick={handleSaveAndClose}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save & Close
                </button>
                <button
                  onClick={handleSaveAndNew}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save & New
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSaveAndClose}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Video Selection Dialog */}
      {showVideoDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Select Video
                </h3>
                <button
                  onClick={closeVideoDialog}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing videos that match:{" "}
                  <span className="font-medium">"{title}"</span>
                </p>
                {loadingVideos ? (
                  <div className="flex items-center space-x-2">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    <p className="text-sm text-gray-500">Loading videos...</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Found {filteredVideos.length} matching videos
                  </p>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {loadingVideos ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
                    <p className="text-gray-500">Searching for videos...</p>
                  </div>
                ) : filteredVideos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No videos found matching "{title}". Try a different video
                    title.
                  </p>
                ) : (
                  filteredVideos.map((video) => (
                    <div
                      key={video.id}
                      className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => selectVideo(video)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">
                            {video.name}
                          </h4>
                          {video.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {video.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>
                              Duration: {Math.floor(video.duration / 60)}:
                              {(video.duration % 60)
                                .toString()
                                .padStart(2, "0")}
                            </span>
                            <span>ID: {video.id}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectVideo(video);
                          }}
                          className="bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={closeVideoDialog}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoEntry;
